import React, { useState, useRef, useEffect } from 'react';
import TopBar from '../../Tools/Topbar';
import LeftSide from '../../Tools/LeftSidebar';
import Settings from '../../Tools/Setting/Settings';
import useSettings from '../../Tools/Setting/useSettings';
import SystemSettings from './Tabs/SystemSettings';
import NotificationSettings from './Tabs/NotificationSettings';
import Integrations from './Tabs/Integrations';
import DataManagement from './Tabs/DataManagement';
import DatabaseConnection from './Tabs/DatabaseConnection';
import ChecklistReminder from './Tabs/ChecklistReminder';
import axiosInstance from "../../../../api";

// Component for managing system configuration settings with a sidebar and tabbed interface
const EMSSystemConfiguration = () => {
  // State to manage sidebar visibility
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  // Custom hook to manage settings and theme
  const { settings, updateSetting, handleColorTheme } = useSettings();
  // Reference to the Settings component
  const settingsRef = useRef();

  // Function to toggle sidebar visibility
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    // Main wrapper for the layout
    <div id="main-wrapper">
      {/* Sidebar component with toggle functionality */}
      <LeftSide isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      {/* Page wrapper with conditional class based on sidebar state */}
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        {/* Top bar with toggle button for sidebar */}
        <TopBar toggleSidebar={toggleSidebar} />
        {/* Main content area */}
        <div className="body-wrapper">
          <div className="container-fluid">
            {/* Settings component for managing application settings */}
            <Settings
              ref={settingsRef}
              updateSetting={updateSetting}
              handleColorTheme={handleColorTheme}
              settings={settings}
            />
            {/* Card container for system configuration tabs */}
            <div className="card w-100 position-relative overflow-hidden">
              {/* Card header with title and subtitle */}
              <div className="px-4 py-3">
                <h5 className="card-title fw-semibold mb-0 lh-sm">System Configuration</h5>
                <p className="card-subtitle mb-0 mt-1">System setup and customization panel</p>
              </div>
              {/* Card body containing the tabbed interface */}
              <div className="card-body p-4">
                {/* Navigation tabs for different configuration sections */}
                <ul className="nav nav-tabs border-bottom border-primary" role="tablist">
                  {/* System Settings tab */}
                  <li className="nav-item" role="presentation">
                    <a className="nav-link d-flex active" data-bs-toggle="tab" href="#system-settings" role="tab">
                      <span className="d-flex align-items-center"><i className="ti ti-settings fs-5"></i></span>
                      <span className="d-none d-md-block ms-2">System Settings</span>
                    </a>
                  </li>
                  {/* Notification Settings tab */}
                  <li className="nav-item" role="presentation">
                    <a className="nav-link d-flex" data-bs-toggle="tab" href="#notification-settings" role="tab">
                      <span><i className="ti ti-bell-ringing fs-5"></i></span>
                      <span className="d-none d-md-block ms-2">Notification Settings</span>
                    </a>
                  </li>
                  {/* Integrations tab */}
                  <li className="nav-item" role="presentation">
                    <a className="nav-link d-flex" data-bs-toggle="tab" href="#integrations" role="tab">
                      <span><i className="ti ti-source-code fs-5"></i></span>
                      <span className="d-none d-md-block ms-2">Integrations</span>
                    </a>
                  </li>
                  {/* Data Management & Security tab */}
                  <li className="nav-item" role="presentation">
                    <a className="nav-link d-flex" data-bs-toggle="tab" href="#data-management" role="tab">
                      <span><i className="ti ti-shield-lock fs-5"></i></span>
                      <span className="d-none d-md-block ms-2">Data Management & Security</span>
                    </a>
                  </li>
                  {/* Database Connection tab */}
                  <li className="nav-item" role="presentation">
                    <a className="nav-link d-flex" data-bs-toggle="tab" href="#database-connection" role="tab">
                      <span><i className="ti ti-database fs-5"></i></span>
                      <span className="d-none d-md-block ms-2">Database Connection</span>
                    </a>
                  </li>
                  {/* Checklist Reminder tab */}
                  <li className="nav-item" role="presentation">
                    <a className="nav-link d-flex" data-bs-toggle="tab" href="#checklist-reminder" role="tab">
                      <span><i className="ti ti-list-check fs-5"></i></span>
                      <span className="d-none d-md-block ms-2">Checklist Reminder</span>
                    </a>
                  </li>
                </ul>

                {/* Tab content area for rendering active tab */}
                <div className="tab-content">
                  <SystemSettings />
                  <NotificationSettings />
                  <Integrations />
                  <DataManagement />
                  <DatabaseConnection />
                  <ChecklistReminder />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export the component as default
export default EMSSystemConfiguration;